import { z } from 'zod';

/**
 * Shared identifier parser + wire contract for GET /api/flow/lookup — Trigger 2
 * (plan D12). Lives in `shared/` (not `api/` or a frontend-only file) so the
 * frontend's identifier-shape detection (GlobalSearch routing to this endpoint
 * instead of fuzzy search) and the backend's classification can never drift
 * apart — both import `parseSearchIdentifier` from here.
 *
 * Classification order matters: an SFDC id's 15/18-char body is alnum and
 * could theoretically satisfy a loose "no spaces" domain check, so the
 * prefix-specific id checks run first and the domain check runs last, after
 * confirming there is no `@` (an email is never domain-shaped by itself).
 */

/**
 * SFDC key-prefix id shape: a 3-character object-type prefix followed by a
 * 12-character body (15-char form) optionally followed by the 3-character
 * case-safety suffix (18-char form). `001` = Account, `003` = Contact.
 *
 * Deliberately duplicated from the local `SFDC_ACCOUNT_ID_PATTERN` in
 * `src/api/flow.ts` (U4b's force-fetch seam) rather than imported from there:
 * that pattern is scoped to the account-only subset that handler needs, and
 * flow.ts is under cross-unit edit contention (plan Q-flow-ts-contention) —
 * duplicating a one-line regex is cheaper than adding a new shared-import
 * coupling to a file three other units are also editing.
 */
const SFDC_ACCOUNT_ID_PATTERN = /^001[a-zA-Z0-9]{12}([a-zA-Z0-9]{3})?$/;
const SFDC_CONTACT_ID_PATTERN = /^003[a-zA-Z0-9]{12}([a-zA-Z0-9]{3})?$/;

/**
 * Any-object SFDC record id shape — the same 15-char body / optional 3-char
 * case-safety suffix as other patterns in this file, with the key prefix
 * left open (custom objects such as `Signal_Action__c` carry an
 * org-assigned prefix like `a0X`). Rejects an empty string, a 16/17-char
 * run, and any non-alphanumeric character (`:`, `/`, whitespace).
 */
const SFDC_RECORD_ID_PATTERN = /^[a-zA-Z0-9]{15}(?:[a-zA-Z0-9]{3})?$/;

/** `true` when `value` is a plausibly shaped 15/18-char Salesforce record id of any object type. */
export function isSfdcRecordId(value: string): boolean {
  return SFDC_RECORD_ID_PATTERN.test(value);
}

/**
 * Dotted-label domain shape — no `@`, no whitespace, at least one `.`
 * separating alnum-and-hyphen labels. `parseDomainLink`
 * (`web/app-home/src/components/flow/links.tsx:52`) is the shape precedent
 * (it recognizes the same "bare hostname" text inside an SFDC HYPERLINK()
 * anchor); this pattern classifies the raw search-box input, not a parsed
 * anchor.
 */
const DOMAIN_SHAPE_PATTERN = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/;

/**
 * Pulls a 15/18-char SFDC Account/Contact id out of a pasted record URL —
 * Lightning (`https://<org>.lightning.force.com/lightning/r/Account/001.../view`)
 * or classic (`https://<org>.my.salesforce.com/001...`). Reps copy links out
 * of their browser bar, not raw ids, so the search box has to accept what
 * they actually have in the clipboard.
 *
 * Only runs against http(s) input (gated in the caller) — this is
 * deliberately not a general "find an id anywhere in this text" scan.
 * Requires non-alphanumeric boundaries on both sides of the id so a longer
 * alnum run (an org hash, a session token in the query string) can't be
 * mistaken for a shorter id embedded inside it.
 */
const URL_SFDC_ID_PATTERN = /(?:^|[^a-zA-Z0-9])(00[13][a-zA-Z0-9]{12}(?:[a-zA-Z0-9]{3})?)(?:[^a-zA-Z0-9]|$)/;

function extractSfdcIdFromUrl(value: string): string | null {
  if (!/^https?:\/\//i.test(value)) return null;
  return value.match(URL_SFDC_ID_PATTERN)?.[1] ?? null;
}

export type SearchIdentifierKind = 'sfdc_account_id' | 'sfdc_contact_id' | 'email' | 'domain' | null;

export interface ParsedSearchIdentifier {
  kind: SearchIdentifierKind;
  /**
   * The trimmed input, in its original casing (SFDC ids are case-sensitive
   * in 15-char form) — EXCEPT for a URL-derived id kind, where this is the
   * extracted 15/18-char id, not the original URL (callers need the bare id
   * to look anything up; the URL itself isn't a usable key).
   */
  value: string;
}

/**
 * Classify a search-box query as a precise identifier, or `null` when it is
 * not identifier-shaped (the caller should fall through to fuzzy search).
 *
 * `null`-kind examples this must reject: plain text ("acme" — no dot), a
 * 15-char id with a non-`00`-prefixed key prefix (e.g. a `006` Opportunity
 * id — SFDC ids for objects this endpoint can't resolve), anything
 * containing whitespace, and a non-SFDC URL (no 001/003 id embedded in it).
 */
export function parseSearchIdentifier(q: string): ParsedSearchIdentifier {
  const value = q.trim();

  if (SFDC_ACCOUNT_ID_PATTERN.test(value)) return { kind: 'sfdc_account_id', value };
  if (SFDC_CONTACT_ID_PATTERN.test(value)) return { kind: 'sfdc_contact_id', value };

  const urlId = extractSfdcIdFromUrl(value);
  if (urlId) {
    if (SFDC_ACCOUNT_ID_PATTERN.test(urlId)) return { kind: 'sfdc_account_id', value: urlId };
    if (SFDC_CONTACT_ID_PATTERN.test(urlId)) return { kind: 'sfdc_contact_id', value: urlId };
  }

  if (value.includes('@')) return { kind: 'email', value };
  if (DOMAIN_SHAPE_PATTERN.test(value)) return { kind: 'domain', value };

  return { kind: null, value };
}

// ── GET /api/flow/lookup response contract ──────────────────────────────────
//
// A discriminated union so a `kind: 'none'` response can never accidentally
// carry a stale/undefined account_id — the schema itself enforces the two
// shapes are mutually exclusive, mirroring how `FlowAccountDetailResponseSchema`
// (`account-view.ts`) enforces its 'synced' / 'resolving' split.
//
// `mirrored: false` + `resolve_triggered: true` is the on-demand force-fetch
// outcome (plan D9/D10/D12, Trigger 2): the target account id is known (pasted
// directly, or derived from a mirrored contact/email row) but has no mirror
// row yet, so `requestAccountResolve` has been fired and the client should
// navigate to `/accounts/<account_id>` and let that page's own polling
// (plan D11) settle it. `name` is therefore absent on that branch — there is
// nothing mirrored yet to read a name from.

export const FlowLookupAccountResultSchema = z.object({
  kind: z.literal('account'),
  account_id: z.string(),
  name: z.string().nullable().optional(),
  mirrored: z.boolean(),
  resolve_triggered: z.boolean(),
});
export type FlowLookupAccountResult = z.infer<typeof FlowLookupAccountResultSchema>;

export const FlowLookupNoneResultSchema = z.object({
  kind: z.literal('none'),
});
export type FlowLookupNoneResult = z.infer<typeof FlowLookupNoneResultSchema>;

export const FlowLookupResponseSchema = z.discriminatedUnion('kind', [
  FlowLookupAccountResultSchema,
  FlowLookupNoneResultSchema,
]);
export type FlowLookupResponse = z.infer<typeof FlowLookupResponseSchema>;
